import React, { FC, useEffect, useMemo } from "react";
import { Select } from "@telia/teddy";
import _keys from "lodash/keys";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "../../../../../../models";
import { CustomerExistingSubscription, SubscriptionsState } from "../../../../../../models/state";
import {
    isBroadbandCode,
    isEquipmentEligible,
    isZeroPlaySubscription,
} from "../../../../../../shared/subscriptionCodeUtils";
import { getAccountId } from "../../../../checkout/state/selectors/accountSelectors";
import { getSelectedPhoneNumber } from "../../../../checkout/state/selectors/phoneNumberSelectors";
import { getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import { getCustomerExistingAccounts } from "../../../state/selectors/customerSelectors";
import _capitalize from "lodash/capitalize";
import { isScopedChange } from "../../../utils";
import "./SubscriptionDropdown.less";

type SubscriptionDropdownVariant = "equipment" | "additionalServices";

interface DropdownItem {
    id: string;
    isExisting: boolean;
    name: string;
    phoneNumber: string;
    isMbb: boolean;
    productUserFirstName?: string;
}

interface Props {
    onSubscriptionChange: (subscriptionId: string, phoneNumber: string, isExisting: boolean) => void;
    preselectPhoneNumber?: string;
    selectedSubscriptionId?: string;
    variant: SubscriptionDropdownVariant;
    shouldIncludeExistingSubscriptions?: boolean;
}

const SubscriptionDropdown: FC<Props> = ({
    selectedSubscriptionId,
    onSubscriptionChange,
    preselectPhoneNumber,
    variant,
    shouldIncludeExistingSubscriptions,
}) => {
    const subscriptions = useSelector(getSubscriptions);

    const selectedBan = useSelector(getAccountId);
    const existingSubscriptions: CustomerExistingSubscription[] = useSelector((state: RootState) => {
        const accounts = getCustomerExistingAccounts(state);
        if (selectedBan) {
            const current = accounts.find((a) => a.accountId === selectedBan);
            return current?.subscriptions || [];
        }
        return accounts.flatMap((account) => account.subscriptions || []);
    }, shallowEqual);

    const bundleSubscriptions = useMemo(() => {
        const filteredSubscriptions: SubscriptionsState = {};
        Object.entries(subscriptions).forEach(([id, sub]) => {
            if (variant === "equipment") {
                if (isEquipmentEligible(sub.code)) {
                    filteredSubscriptions[id] = sub;
                }
            } else if (variant === "additionalServices") {
                if (!isBroadbandCode(sub.code) && !isZeroPlaySubscription(sub.code)) {
                    filteredSubscriptions[id] = sub;
                }
            }
        });
        return filteredSubscriptions;
    }, [subscriptions, variant]);

    const phoneNumbersMap = useSelector((state: RootState) => {
        const subscriptionIds = _keys(bundleSubscriptions);
        const phoneNumbers: Record<string, string> = {};
        subscriptionIds.forEach((subscriptionId) => {
            const phoneNumber = getSelectedPhoneNumber(state, subscriptionId);
            phoneNumbers[subscriptionId] = phoneNumber;
        });
        return phoneNumbers;
    });

    const dropdownItems: DropdownItem[] = useMemo(() => {
        const items: DropdownItem[] = [];
        const seenPhoneNumbers = new Set<string>();

        // Cart subscriptions
        for (const id of _keys(bundleSubscriptions)) {
            const sub = bundleSubscriptions[id];
            const phoneNumber = phoneNumbersMap[id] || "";
            if (isScopedChange(sub.existingSubscriptionUpdateScope)) continue;
            if (phoneNumber) seenPhoneNumbers.add(phoneNumber);
            items.push({
                id,
                isExisting: false,
                name: sub.name,
                phoneNumber,
                isMbb: isBroadbandCode(sub.code),
                productUserFirstName: sub.productUser?.firstName,
            });
        }

        if (!shouldIncludeExistingSubscriptions) return items;

        // Existing subscriptions
        for (const sub of existingSubscriptions) {
            if (seenPhoneNumbers.has(sub.phoneNumber)) continue;
            const matchesVariant =
                variant === "equipment"
                    ? isEquipmentEligible(sub.offeringCode)
                    : !isBroadbandCode(sub.offeringCode) && !isZeroPlaySubscription(sub.offeringCode);
            if (!matchesVariant) continue;

            seenPhoneNumbers.add(sub.phoneNumber);
            items.push({
                id: sub.phoneNumber,
                isExisting: true,
                name: sub.offeringName || "",
                phoneNumber: sub.phoneNumber,
                isMbb: isBroadbandCode(sub.offeringCode),
                productUserFirstName: sub.firstName,
            });
        }

        return items;
    }, [bundleSubscriptions, existingSubscriptions, phoneNumbersMap, variant, shouldIncludeExistingSubscriptions]);

    const itemIds = useMemo(() => dropdownItems.map((item) => item.id), [dropdownItems]);

    const handleChange = (value: string) => {
        if (value) {
            const item = dropdownItems.find((i) => i.id === value);
            onSubscriptionChange(value, item?.phoneNumber || "", item?.isExisting ?? false);
        }
        return value;
    };

    useEffect(() => {
        if (preselectPhoneNumber && selectedSubscriptionId === undefined) {
            const item = dropdownItems.find((i) => i.phoneNumber === preselectPhoneNumber);
            if (item) {
                onSubscriptionChange(item.id, item.phoneNumber, item.isExisting);
                return;
            }
        }
        if (itemIds.length === 1 && selectedSubscriptionId !== itemIds[0]) {
            const item = dropdownItems[0];
            onSubscriptionChange(item.id, item.phoneNumber, item.isExisting);
        }
    }, [itemIds, selectedSubscriptionId, dropdownItems, onSubscriptionChange, preselectPhoneNumber]);

    return (
        <div className="subscription-dropdown">
            <Select
                name="subscription"
                options={itemIds}
                value={selectedSubscriptionId || ""}
                onValueChange={handleChange}
                className="subscription-dropdown__select"
            >
                <Select.Trigger>
                    <Select.Value placeholder="Velg et abonnement" />
                </Select.Trigger>
                <Select.Content style={{ zIndex: 10 }}>
                    <Select.Viewport>
                        {dropdownItems.map((item) => (
                            <Select.Item key={item.id} value={item.id}>
                                {item.isMbb && "MBB: "}
                                {item.name}
                                {!item.isMbb && item.phoneNumber && ` - ${item.phoneNumber}`}
                                {item.productUserFirstName && ` (${_capitalize(item.productUserFirstName)})`}
                            </Select.Item>
                        ))}
                    </Select.Viewport>
                </Select.Content>
            </Select>
        </div>
    );
};

export default SubscriptionDropdown;
